import React, { useState } from 'react';
import { useStripe, useElements, PaymentElement, AddressElement, ExpressCheckoutElement, LinkAuthenticationElement } from '@stripe/react-stripe-js';
import styles from './StripeTestCheckout.module.scss';
import PayPal from '../Checkout/PayPal';
import afterpayLogoWhite from '../../../assets/afterpay-white.png';
import afterpayLogoDark from '../../../assets/afterpay.png';

const MINIMUM_AMOUNT_FOR_AFTERPAY = 35;

const CheckoutForm = ({ email, amount, onSuccess, onError, cartId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [showShipping, setShowShipping] = useState(false);

  // Shipping address from Stripe AddressElement
  const [shippingAddress, setShippingAddress] = useState(null);
  const [shippingComplete, setShippingComplete] = useState(false);

  // Afterpay express flow
  const [afterpaySelected, setAfterpaySelected] = useState(false);
  const showAfterpayButton = parseFloat(amount) >= MINIMUM_AMOUNT_FOR_AFTERPAY;

  // Link express flow
  const [linkSelected, setLinkSelected] = useState(false);
  const [linkEmail, setLinkEmail] = useState(email || '');

  // PayPal handlers
  const createPayPalOrder = (data, actions) => actions.order.create({
    purchase_units: [
      {
        amount: {
          value: amount || '0.01',
        },
      },
    ],
  });

  const handlePayPalApprove = async (data, actions) => {
    setIsProcessing(true);
    try {
      const order = await actions.order.get();

      // Extract payer info from PayPal
      const payer = order.payer || {};
      const shipping = order.purchase_units?.[0]?.shipping?.address || {};
      const payerName = payer.name || {};

      // Store in sessionStorage for potential redirect handling
      if (cartId) {
        sessionStorage.setItem('test_checkout_cart_id', cartId);
        sessionStorage.setItem('test_checkout_email', payer.email_address || email);
        sessionStorage.setItem('test_checkout_customer_data', JSON.stringify({
          firstName: payerName.given_name || '',
          lastName: payerName.surname || '',
          phone: payer.phone?.phone_number?.national_number || '',
          shippingAddress: {
            address1: shipping.address_line_1 || '',
            address2: shipping.address_line_2 || '',
            city: shipping.admin_area_2 || '',
            state: shipping.admin_area_1 || '',
            zip: shipping.postal_code || '',
          },
        }));
      }

      // Capture the PayPal payment
      const captureResult = await actions.order.capture();

      if (captureResult.status !== 'COMPLETED') {
        onError('PayPal payment was not completed. Please try again.');
        setIsProcessing(false);
        return;
      }

      // Payment successful - call onSuccess with customer data
      await onSuccess(
        { id: captureResult.id, paypal: true },
        {
          firstName: payerName.given_name || '',
          lastName: payerName.surname || '',
          phone: payer.phone?.phone_number?.national_number || '',
          billingAddress: {
            address1: shipping.address_line_1 || '',
            address2: shipping.address_line_2 || '',
            city: shipping.admin_area_2 || '',
            state: shipping.admin_area_1 || '',
            zip: shipping.postal_code || '',
          },
          shippingAddress: {
            address1: shipping.address_line_1 || '',
            address2: shipping.address_line_2 || '',
            city: shipping.admin_area_2 || '',
            state: shipping.admin_area_1 || '',
            zip: shipping.postal_code || '',
          },
        },
      );
      setIsProcessing(false);
    } catch (err) {
      console.error('PayPal error:', err);
      onError('An error occurred with PayPal. Please try again.');
      setIsProcessing(false);
    }
  };

  // Link handler
  const handleLinkClick = () => {
    setLinkSelected(true);
    setAfterpaySelected(false);
    setShowPaymentDetails(false);
    setShowShipping(false);
  };


  // Afterpay handler
  const handleAfterpayClick = () => {
    setAfterpaySelected(true);
    setLinkSelected(false);
    setShowPaymentDetails(false);
    setShowShipping(true); // Go straight to shipping
  };

  const handleAfterpaySubmit = async () => {
    if (!stripe || !shippingAddress || !shippingComplete) {
      onError('Please complete the shipping address');
      return;
    }

    if (!email) {
      onError('Email is required');
      return;
    }

    setIsProcessing(true);

    // Store session data for redirect return
    if (cartId) {
      sessionStorage.setItem('test_checkout_cart_id', cartId);
      sessionStorage.setItem('test_checkout_email', email);
      sessionStorage.setItem('test_checkout_customer_data', JSON.stringify({
        firstName: shippingAddress.firstName || '',
        lastName: shippingAddress.lastName || '',
        phone: shippingAddress.phone || '',
        shippingAddress: {
          address1: shippingAddress.line1 || '',
          address2: shippingAddress.line2 || '',
          city: shippingAddress.city || '',
          state: shippingAddress.state || '',
          zip: shippingAddress.postal_code || '',
        },
      }));
    }

    try {
      // Create payment intent for Afterpay
      const response = await fetch('/api/v1/test-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: Math.round(parseFloat(amount) * 100),
          currency: 'usd',
          payment_method_type: 'afterpay_clearpay',
        }),
      });

      const { client_secret, error: intentError } = await response.json();

      if (intentError) {
        onError(intentError);
        setIsProcessing(false);
        return;
      }

      // Confirm Afterpay payment (redirects to Afterpay)
      const { error: stripeError } = await stripe.confirmAfterpayClearpayPayment(client_secret, {
        payment_method: {
          billing_details: {
            name: `${shippingAddress.firstName || ''} ${shippingAddress.lastName || ''}`.trim(),
            email,
            address: {
              line1: shippingAddress.line1 || '',
              line2: shippingAddress.line2 || '',
              city: shippingAddress.city || '',
              state: shippingAddress.state || '',
              country: 'US',
              postal_code: shippingAddress.postal_code || '',
            },
          },
        },
        shipping: {
          name: `${shippingAddress.firstName || ''} ${shippingAddress.lastName || ''}`.trim(),
          phone: shippingAddress.phone || '',
          address: {
            line1: shippingAddress.line1 || '',
            line2: shippingAddress.line2 || '',
            city: shippingAddress.city || '',
            state: shippingAddress.state || '',
            country: 'US',
            postal_code: shippingAddress.postal_code || '',
          },
        },
        return_url: `${window.location.origin}/c/test-checkout/complete`,
      });

      if (stripeError) {
        sessionStorage.removeItem('test_checkout_cart_id');
        sessionStorage.removeItem('test_checkout_email');
        sessionStorage.removeItem('test_checkout_customer_data');
        onError(stripeError.message || 'Afterpay payment failed. Please try again.');
        setIsProcessing(false);
      }
      // If no error, user was redirected to Afterpay
    } catch (err) {
      console.error('Afterpay error:', err);
      sessionStorage.removeItem('test_checkout_cart_id');
      sessionStorage.removeItem('test_checkout_email');
      sessionStorage.removeItem('test_checkout_customer_data');
      onError('An error occurred with Afterpay. Please try again.');
      setIsProcessing(false);
    }
  };

  // Express checkout handler
  const handleExpressCheckoutConfirm = async (event) => {
    if (!stripe || !elements) return;

    setIsProcessing(true);

    const { billingDetails, shippingAddress: expressShipping } = event;

    // Store cart and customer data in sessionStorage (same pattern as manual flow)
    if (cartId) {
      const expressEmail = billingDetails?.email || '';
      sessionStorage.setItem('test_checkout_cart_id', cartId);
      sessionStorage.setItem('test_checkout_email', expressEmail);

      const nameParts = (billingDetails?.name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      const shipping = expressShipping?.address || billingDetails?.address || {};

      sessionStorage.setItem('test_checkout_customer_data', JSON.stringify({
        firstName,
        lastName,
        phone: billingDetails?.phone || expressShipping?.phone || '',
        shippingAddress: {
          address1: shipping.line1 || '',
          address2: shipping.line2 || '',
          city: shipping.city || '',
          state: shipping.state || '',
          zip: shipping.postal_code || ''
        }
      }));
    }

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/c/test-checkout/complete`,
        },
        redirect: 'if_required',
      });

      if (error) {
        sessionStorage.removeItem('test_checkout_cart_id');
        sessionStorage.removeItem('test_checkout_email');
        sessionStorage.removeItem('test_checkout_customer_data');
        onError(error.message);
        setIsProcessing(false);
      } else {
        // Payment succeeded — redirect will handle completion,
        // or if no redirect, call onSuccess
        const expressEmail = billingDetails?.email || '';
        const nameParts = (billingDetails?.name || '').split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        const shipping = expressShipping?.address || billingDetails?.address || {};

        await onSuccess(null, {
          firstName,
          lastName,
          phone: billingDetails?.phone || expressShipping?.phone || '',
          billingAddress: {
            address1: (billingDetails?.address?.line1) || '',
            address2: (billingDetails?.address?.line2) || '',
            city: (billingDetails?.address?.city) || '',
            state: (billingDetails?.address?.state) || '',
            zip: (billingDetails?.address?.postal_code) || ''
          },
          shippingAddress: {
            address1: shipping.line1 || '',
            address2: shipping.line2 || '',
            city: shipping.city || '',
            state: shipping.state || '',
            zip: shipping.postal_code || ''
          }
        });
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('Express checkout error:', err);
      sessionStorage.removeItem('test_checkout_cart_id');
      sessionStorage.removeItem('test_checkout_email');
      sessionStorage.removeItem('test_checkout_customer_data');
      onError('An unexpected error occurred: ' + (err.message || 'Unknown error'));
      setIsProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // If payment details aren't shown yet, show them
    if (!showPaymentDetails) {
      setShowPaymentDetails(true);
      return;
    }

    // If shipping isn't shown yet, show it
    if (!showShipping) {
      setShowShipping(true);
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    if (!shippingComplete || !shippingAddress) {
      onError('Please complete the shipping address');
      return;
    }

    setIsProcessing(true);

    // Store cart, email, and customer data in sessionStorage for redirect callback
    const checkoutEmail = linkSelected ? linkEmail : email;
    if (cartId) {
      sessionStorage.setItem('test_checkout_cart_id', cartId);
      sessionStorage.setItem('test_checkout_email', checkoutEmail);
      sessionStorage.setItem('test_checkout_customer_data', JSON.stringify({
        firstName: shippingAddress.firstName || '',
        lastName: shippingAddress.lastName || '',
        phone: shippingAddress.phone || '',
        shippingAddress: {
          address1: shippingAddress.line1 || '',
          address2: shippingAddress.line2 || '',
          city: shippingAddress.city || '',
          state: shippingAddress.state || '',
          zip: shippingAddress.postal_code || ''
        }
      }));
    }

    try {
      console.log('Starting payment confirmation...');
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/c/test-checkout/complete`,
          receipt_email: checkoutEmail,
          shipping: {
            name: `${shippingAddress.firstName || ''} ${shippingAddress.lastName || ''}`.trim(),
            phone: shippingAddress.phone || '',
            address: {
              line1: shippingAddress.line1 || '',
              line2: shippingAddress.line2 || '',
              city: shippingAddress.city || '',
              state: shippingAddress.state || '',
              postal_code: shippingAddress.postal_code || '',
              country: shippingAddress.country || 'US',
            },
          },
        },
        redirect: 'if_required',
      });

      console.log('Payment confirmation result:', { error, paymentIntent });

      if (error) {
        // Clear session storage on error
        sessionStorage.removeItem('test_checkout_cart_id');
        sessionStorage.removeItem('test_checkout_email');
        sessionStorage.removeItem('test_checkout_customer_data');
        onError(error.message);
        setIsProcessing(false);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Fetch payment intent with expanded payment method to get billing details
        const response = await fetch(`/api/v1/payment-intent/${paymentIntent.id}`);
        const paymentIntentData = await response.json();

        // Extract billing details from payment method
        const billingDetails = paymentIntentData.payment_method?.billing_details || {};
        const billingAddress = billingDetails.address || {};

        await onSuccess(paymentIntent, {
          firstName: shippingAddress.firstName || '',
          lastName: shippingAddress.lastName || '',
          phone: shippingAddress.phone || '',
          billingAddress: {
            address1: billingAddress.line1 || '',
            address2: billingAddress.line2 || '',
            city: billingAddress.city || '',
            state: billingAddress.state || '',
            zip: billingAddress.postal_code || ''
          },
          shippingAddress: {
            address1: shippingAddress.line1 || '',
            address2: shippingAddress.line2 || '',
            city: shippingAddress.city || '',
            state: shippingAddress.state || '',
            zip: shippingAddress.postal_code || ''
          }
        });
        setIsProcessing(false);
      } else {
        console.log('Unexpected payment state:', paymentIntent);
        onError('Payment completed but status is unexpected. Please contact support.');
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('Payment error:', err);
      // Clear session storage on error
      sessionStorage.removeItem('test_checkout_cart_id');
      sessionStorage.removeItem('test_checkout_email');
      sessionStorage.removeItem('test_checkout_customer_data');
      onError('An unexpected error occurred: ' + (err.message || 'Unknown error'));
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.expressCheckoutSection}>
        <p className={styles.expressCheckoutTitle}>Express checkout</p>
        <ExpressCheckoutElement
          options={{
            paymentMethods: {
              applePay: 'auto',
              googlePay: 'auto',
            },
            layout: {
              overflow: 'auto',
            },
          }}
          onConfirm={handleExpressCheckoutConfirm}
          onCancel={() => {}}
        />
        <div className={styles.expressButtons}>
          <div className={styles.paypalButton}>
            <PayPal
              createOrder={createPayPalOrder}
              onApprove={handlePayPalApprove}
            />
          </div>
          <button
            type="button"
            onClick={handleLinkClick}
            className={styles.linkButton}
            disabled={linkSelected}
          >
            <span className={styles.linkLogo}>link</span>
          </button>
          {showAfterpayButton && (
            <button
              type="button"
              onClick={handleAfterpayClick}
              className={styles.afterpayButton}
              disabled={afterpaySelected}
            >
              <span className={styles.afterpayButtonText}>Checkout with </span>
              <img src={afterpayLogoWhite} alt="Afterpay" className={styles.afterpayLogo} />
            </button>
          )}
        </div>
      </div>

      {!afterpaySelected && !linkSelected && (
        <div className={styles.divider}>
          <span>OR</span>
        </div>
      )}

      {linkSelected && (
        <div className={styles.section}>
          <div className={styles.linkHeader}>
            <h2 className={styles.sectionTitle}>1. Email for Link</h2>
            <button
              type="button"
              onClick={() => {
                setLinkSelected(false);
                setShowPaymentDetails(false);
                setShowShipping(false);
                setLinkEmail(email || '');
              }}
              className={styles.cancelLink}
            >
              Cancel
            </button>
          </div>
          <p className={styles.linkInfo}>Enter your email to use Link. If you have a Link account, we'll text you a code to autofill your saved payment and shipping info.</p>
          <div className={styles.linkAuthWrapper}>
            <LinkAuthenticationElement
              options={{
                defaultValues: {
                  email: linkEmail,
                },
              }}
              onChange={(e) => {
                if (e.value?.email) {
                  setLinkEmail(e.value.email);
                }
                // Auto-advance when Link is ready
                if (e.complete && !showPaymentDetails) {
                  setShowPaymentDetails(true);
                  setShowShipping(true);
                }
              }}
            />
          </div>
        </div>
      )}

      {!afterpaySelected && !linkSelected && !showPaymentDetails && (
        <div className={styles.section}>
          <button
            type="button"
            onClick={() => setShowPaymentDetails(true)}
            className={styles.button}
          >
            Continue to Payment
          </button>
        </div>
      )}

      {showPaymentDetails && !afterpaySelected && (
        <>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>{linkSelected ? '2. Payment Details' : '1. Payment Details'}</h2>
            <p className={styles.paymentInfo}>
              Supports credit cards, Apple Pay, Google Pay, PayPal, Link, and Afterpay (min. $35)
            </p>
            <div className={styles.paymentElement}>
              <PaymentElement
                options={{
                  layout: 'tabs',
                  wallets: {
                    applePay: 'auto',
                    googlePay: 'auto',
                  },
                  fields: {
                    billingDetails: {
                      address: 'auto',
                    },
                  },
                  defaultValues: {
                    billingDetails: {
                      email: linkSelected ? linkEmail : email,
                    },
                  },
                }}
              />
            </div>
          </div>

          {!showShipping && !linkSelected && (
            <div className={styles.section}>
              <button
                type="submit"
                disabled={isProcessing || !stripe || !elements}
                className={styles.button}
              >
                Continue to Shipping
              </button>
            </div>
          )}
        </>
      )}

      {showShipping && (
        <>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>{afterpaySelected ? '1. Shipping Address' : (linkSelected ? '3. Shipping Address' : '2. Shipping Address')}</h2>
            <div className={styles.addressElement}>
              <AddressElement
                options={{
                  mode: 'shipping',
                  allowedCountries: ['US'],
                  fields: {
                    phone: 'always',
                  },
                  validation: {
                    phone: {
                      required: 'always',
                    },
                  },
                  display: {
                    name: 'split',
                  },
                }}
                onChange={(event) => {
                  if (event.complete) {
                    const address = event.value.address;
                    setShippingAddress({
                      ...address,
                      name: event.value.name,
                      firstName: event.value.firstName,
                      lastName: event.value.lastName,
                      phone: event.value.phone,
                    });
                    setShippingComplete(true);
                  } else {
                    setShippingComplete(false);
                  }
                }}
              />
            </div>
          </div>

          <div className={styles.section}>
            {afterpaySelected ? (
              <button
                type="button"
                onClick={handleAfterpaySubmit}
                disabled={isProcessing || !stripe || !shippingComplete}
                className={styles.afterpaySubmitButton}
              >
                {isProcessing ? 'Processing...' : (
                  <>
                    Continue with <img src={afterpayLogoDark} alt="Afterpay" className={styles.afterpayLogoSmall} />
                  </>
                )}
              </button>
            ) : (
              <button
                type="submit"
                disabled={isProcessing || !stripe || !elements || !shippingComplete}
                className={styles.submitButton}
              >
                {isProcessing ? 'Processing...' : `Pay $${amount}`}
              </button>
            )}
          </div>
        </>
      )}
    </form>
  );
};

export default CheckoutForm;
